import type { ReferenceCategory } from './guidedTypes';

/**
 * A SMALL, EPN-authored subset of the reference options the real API seeds (SeedData/job-role-reference-options.json), consistent with the
 * contract's categories. It exists only so the mock gateway (demos and tests) behaves like the server: the components never import it, and
 * no component holds an option list of its own. Names are widely used, generic terms; credentials are real, well-known names and every
 * certification or license option says it is only an example, never a legal requirement.
 */

export const MOCK_REFERENCE_DATASET_VERSION = 'EPN-2026.10';

export interface MockReferenceOptionRecord {
  category: ReferenceCategory;
  code: string;
  name: string;
  description: string;
  /** The functional area the option is filed under. */
  group: string;
  synonyms: string[];
}

const EXAMPLE_CREDENTIAL = 'An example credential. Choosing it does not mean the law, a regulator or your company requires it; you decide what your role asks for.';

type Row = [name: string, group: string, description?: string | undefined, synonyms?: string[]];

function make(category: ReferenceCategory, prefix: string, rows: Row[], defaultDescription: string): MockReferenceOptionRecord[] {
  return rows.map(([name, group, description, synonyms], i) => ({
    category, code: `${prefix}-${String(i + 1).padStart(3, '0')}`, name, description: description ?? defaultDescription, group, synonyms: synonyms ?? [],
  }));
}

const FAMILIES: Row[] = [
  ['Executive Leadership', 'Job families', 'Roles that set direction for the whole company.', ['Executive', 'C-Suite']],
  ['Finance and Accounting', 'Job families', 'Roles that keep the books, plan the budget and report results.', ['Finance', 'Accounting']],
  ['Payroll Operations', 'Job families', 'Roles that prepare, check and reconcile payroll.', ['Payroll']],
  ['Tax and Compliance', 'Job families', 'Roles that keep the company in step with tax and regulatory duties.', ['Tax', 'Compliance']],
  ['Human Resources', 'Job families', 'Roles that hire, support and develop employees.', ['HR', 'People Operations']],
  ['Legal', 'Job families', 'Roles that give legal advice and manage legal matters.'],
  ['Risk and Internal Audit', 'Job families', 'Roles that review controls and manage risk.', ['Audit', 'Risk']],
  ['Banking and Treasury', 'Job families', 'Roles that manage cash, credit and banking relationships.', ['Treasury']],
  ['Sales', 'Job families', 'Roles that win and keep customers.'],
  ['Marketing', 'Job families', 'Roles that build the brand and generate demand.'],
  ['Customer Success', 'Job families', 'Roles that help customers get lasting value from the product or service.'],
  ['Customer Support', 'Job families', 'Roles that answer customer questions and fix problems.', ['Customer Service']],
  ['Operations', 'Job families', 'Roles that run the day-to-day work of the business.'],
  ['Administration', 'Job families', 'Roles that provide office and administrative support.', ['Administrative']],
  ['Information Technology', 'Job families', 'Roles that run the company’s systems and technology.', ['IT']],
  ['Software Engineering', 'Job families', 'Roles that design, build and test software.', ['Engineering', 'Software Development']],
  ['Data and Analytics', 'Job families', 'Roles that turn data into decisions.', ['Analytics', 'Data Science']],
  ['Information Security', 'Job families', 'Roles that protect systems and data.', ['Cybersecurity', 'InfoSec']],
  ['Product Management', 'Job families', 'Roles that decide what to build and why.'],
  ['Project Management', 'Job families', 'Roles that plan and deliver projects.', ['Program Management']],
  ['Healthcare', 'Job families', 'Roles that provide or support patient care.'],
  ['Education and Training', 'Job families', 'Roles that teach, train and develop people.', ['Training']],
  ['Manufacturing', 'Job families', 'Roles that make products.', ['Production']],
  ['Construction and Skilled Trades', 'Job families', 'Roles that build, install and repair.', ['Trades']],
  ['Transportation and Logistics', 'Job families', 'Roles that move people and goods.', ['Logistics']],
  ['Hospitality', 'Job families', 'Roles that serve guests in hotels, restaurants and venues.'],
  ['Retail', 'Job families', 'Roles that sell goods in stores and online.'],
  ['Professional Services', 'Job families', 'Roles that deliver advice and projects to clients.', ['Consulting']],
  ['Other', 'Job families', 'A family that does not fit the others.'],
  // Specializations, filed under the functional area they belong to.
  ['Accounts Payable', 'Finance and Accounting', 'Paying the company’s bills accurately and on time.'],
  ['Accounts Receivable', 'Finance and Accounting', 'Billing customers and collecting what they owe.'],
  ['Financial Planning and Analysis', 'Finance and Accounting', 'Budgeting, forecasting and analysis.', ['FP&A']],
  ['Payroll Processing', 'Payroll Operations', 'Running each payroll cycle.'],
  ['Payroll Tax', 'Payroll Operations', 'Payroll tax filings and deposits.'],
  ['Talent Acquisition', 'Human Resources', 'Recruiting and hiring.', ['Recruiting']],
  ['Compensation and Benefits', 'Human Resources', 'Pay structures and benefit programs.', ['Total Rewards']],
  ['Employee Relations', 'Human Resources', 'Resolving workplace concerns and supporting managers.'],
  ['IT Support', 'Information Technology', 'Helping employees with their devices and accounts.', ['Help Desk']],
  ['Systems Administration', 'Information Technology', 'Running servers, accounts and networks.'],
  ['Back-End Development', 'Software Engineering', 'Server-side software and services.'],
  ['Front-End Development', 'Software Engineering', 'The part of software people see and use.'],
  ['Quality Assurance', 'Software Engineering', 'Testing software before it ships.', ['QA', 'Testing']],
  ['Inside Sales', 'Sales', 'Selling by phone, email and chat.'],
  ['Account Management', 'Sales', 'Looking after existing customer accounts.'],
];

const LEVELS: Row[] = [
  ['Intern / Trainee', 'Career levels', 'A temporary or learning role while building experience.', ['Intern', 'Trainee']],
  ['Entry', 'Career levels', 'A first role in the field, learning the work.', ['Entry Level']],
  ['Junior', 'Career levels', 'Early career, working with guidance.'],
  ['Associate', 'Career levels', 'Works on defined tasks and is building independence.'],
  ['Intermediate', 'Career levels', 'Works independently on most tasks.', ['Mid Level']],
  ['Mid-Level', 'Career levels', 'Fully experienced in the role.'],
  ['Senior', 'Career levels', 'Handles complex work and helps others.'],
  ['Lead', 'Career levels', 'Guides the work of others without being their manager.'],
  ['Principal', 'Career levels', 'A recognized expert who shapes how the work is done.'],
  ['Supervisor', 'Career levels', 'Directs the daily work of a team.'],
  ['Manager', 'Career levels', 'Accountable for a team and its results.'],
  ['Senior Manager', 'Career levels', 'Manages managers or a large team.'],
  ['Director', 'Career levels', 'Leads a function or several teams.'],
  ['Senior Director', 'Career levels', 'Leads a large function.'],
  ['Vice President', 'Career levels', 'Leads a major part of the company.', ['VP']],
  ['C-Suite / Executive', 'Career levels', 'A member of the company’s top leadership.', ['Executive', 'C-Suite']],
];

const MANAGEMENT: Row[] = [
  ['Individual Contributor', 'Management levels', 'Does not manage other people.'],
  ['Team Lead', 'Management levels', 'Guides a small team day to day.'],
  ['Supervisor', 'Management levels', 'Directs the work of a team.'],
  ['Assistant Manager', 'Management levels', 'Supports a manager in running a team.'],
  ['Manager', 'Management levels', 'Manages a team.'],
  ['Senior Manager', 'Management levels', 'Manages managers or a large team.'],
  ['Associate Director', 'Management levels', 'Supports a director in leading a function.'],
  ['Director', 'Management levels', 'Leads a function.'],
  ['Senior Director', 'Management levels', 'Leads a large function.'],
  ['Vice President', 'Management levels', 'Leads a major part of the company.', ['VP']],
  ['Senior Vice President', 'Management levels', 'Leads several major parts of the company.', ['SVP']],
  ['Executive Vice President', 'Management levels', 'Leads a group of functions.', ['EVP']],
  ['C-Level Executive', 'Management levels', 'Leads the whole company or a core function.', ['C-Suite']],
  ['Owner or Principal', 'Management levels', 'Owns or co-owns the company.'],
  ['Not Applicable', 'Management levels', 'The role has no management responsibility.', ['N/A', 'None']],
];

const EDUCATION: Row[] = [
  ['No formal education required', 'Education', undefined, ['None']],
  ['High school diploma or equivalent', 'Education', undefined, ['High school', 'GED']],
  ['Vocational or technical certificate', 'Education', undefined, ['Trade school']],
  ['Some college', 'Education'],
  ['Associate degree', 'Education', undefined, ['Associate']],
  ['Bachelor’s degree', 'Education', undefined, ['Bachelor', 'BA', 'BS', 'Undergraduate degree']],
  ['Master’s degree', 'Education', undefined, ['Master', 'MA', 'MS', 'MBA']],
  ['Professional degree', 'Education', 'For example a law or medical degree.', ['JD', 'MD']],
  ['Doctoral degree', 'Education', undefined, ['Doctorate', 'PhD']],
  ['Equivalent experience accepted', 'Education', 'Relevant work experience can stand in for formal education.'],
];

const EXPERIENCE: Row[] = [
  ['None required', 'Experience'],
  ['Less than 1 year', 'Experience'],
  ['1-2 years', 'Experience', undefined, ['1 to 2 years']],
  ['2-4 years', 'Experience', undefined, ['2 to 4 years']],
  ['3-5 years', 'Experience', undefined, ['3 to 5 years']],
  ['5-7 years', 'Experience', undefined, ['5 to 7 years']],
  ['7-10 years', 'Experience', undefined, ['7 to 10 years']],
  ['10+ years', 'Experience', undefined, ['10 or more years']],
];

const SKILLS: Row[] = [
  ['Payroll software', 'Technical skills'], ['Spreadsheets', 'Technical skills', undefined, ['Excel']], ['Reconciliation', 'Technical skills'],
  ['Internal controls', 'Technical skills'], ['General ledger software', 'Technical skills'], ['HR records systems', 'Technical skills', undefined, ['HRIS']],
  ['Scheduling', 'Technical skills'], ['Process improvement', 'Technical skills'], ['CRM software', 'Technical skills', undefined, ['CRM']],
  ['Presentations', 'Technical skills'], ['Ticketing software', 'Technical skills', undefined, ['Help desk software']], ['Typing', 'Technical skills'],
  ['Programming', 'Technical skills', undefined, ['Coding']], ['Testing', 'Technical skills'], ['Version control', 'Technical skills', undefined, ['Git']],
  ['Financial planning', 'Technical skills'], ['Financial reporting', 'Technical skills'], ['Budgeting', 'Technical skills'], ['Data analysis', 'Technical skills'],
  ['Accounts payable processing', 'Technical skills'], ['Payroll tax compliance', 'Technical skills'], ['Recruiting', 'Technical skills'],
  ['Attention to detail', 'Professional skills'], ['Confidentiality', 'Professional skills'], ['Coaching', 'Professional skills'],
  ['Problem solving', 'Professional skills'], ['Leadership', 'Professional skills'], ['Communication', 'Professional skills'], ['Accuracy', 'Professional skills'],
  ['Time management', 'Professional skills'], ['Discretion', 'Professional skills'], ['Organization', 'Professional skills', undefined, ['Organisation']],
  ['Decision making', 'Professional skills'], ['Persuasion', 'Professional skills'], ['Listening', 'Professional skills'], ['Empathy', 'Professional skills'],
  ['Clear writing', 'Professional skills'], ['Collaboration', 'Professional skills', undefined, ['Teamwork']], ['Strategic thinking', 'Professional skills'],
  ['Executive communication', 'Professional skills'], ['Customer service', 'Professional skills'], ['Negotiation', 'Professional skills'],
];

const CERTIFICATIONS: Row[] = [
  ['Certified Payroll Professional (CPP)', 'Payroll and finance', EXAMPLE_CREDENTIAL, ['CPP']],
  ['Fundamental Payroll Certification (FPC)', 'Payroll and finance', EXAMPLE_CREDENTIAL, ['FPC']],
  ['Certified Public Accountant (CPA)', 'Payroll and finance', EXAMPLE_CREDENTIAL, ['CPA']],
  ['Certified Management Accountant (CMA)', 'Payroll and finance', EXAMPLE_CREDENTIAL, ['CMA']],
  ['Certified Internal Auditor (CIA)', 'Payroll and finance', EXAMPLE_CREDENTIAL, ['CIA']],
  ['Certified Fraud Examiner (CFE)', 'Payroll and finance', EXAMPLE_CREDENTIAL, ['CFE']],
  ['Certified Treasury Professional (CTP)', 'Payroll and finance', EXAMPLE_CREDENTIAL, ['CTP']],
  ['Chartered Financial Analyst (CFA)', 'Payroll and finance', EXAMPLE_CREDENTIAL, ['CFA']],
  ['SHRM Certified Professional (SHRM-CP)', 'Human resources', EXAMPLE_CREDENTIAL, ['SHRM-CP']],
  ['SHRM Senior Certified Professional (SHRM-SCP)', 'Human resources', EXAMPLE_CREDENTIAL, ['SHRM-SCP']],
  ['Professional in Human Resources (PHR)', 'Human resources', EXAMPLE_CREDENTIAL, ['PHR']],
  ['Senior Professional in Human Resources (SPHR)', 'Human resources', EXAMPLE_CREDENTIAL, ['SPHR']],
  ['Project Management Professional (PMP)', 'Project management', EXAMPLE_CREDENTIAL, ['PMP']],
  ['Certified Associate in Project Management (CAPM)', 'Project management', EXAMPLE_CREDENTIAL, ['CAPM']],
  ['Six Sigma Green Belt', 'Operations', EXAMPLE_CREDENTIAL, ['Green Belt']],
  ['Certified Information Systems Security Professional (CISSP)', 'Technology', EXAMPLE_CREDENTIAL, ['CISSP']],
  ['Certified Information Systems Auditor (CISA)', 'Technology', EXAMPLE_CREDENTIAL, ['CISA']],
  ['CompTIA A+', 'Technology', EXAMPLE_CREDENTIAL, ['A+']],
  ['CompTIA Security+', 'Technology', EXAMPLE_CREDENTIAL, ['Security+']],
];

const LICENSES: Row[] = [
  ['Driver’s license', 'Licenses', 'A valid driver’s license, as an example of a license a role might ask for. Choosing it does not mean the law requires it.'],
  ['Commercial driver’s license (CDL)', 'Licenses', 'An example license. Choosing it does not mean the law requires it for this role.', ['CDL']],
  ['Registered nurse (RN) license', 'Licenses', 'An example license. Choosing it does not mean the law requires it for this role.', ['RN']],
  ['State bar admission', 'Licenses', 'An example license. Choosing it does not mean the law requires it for this role.', ['Bar admission']],
  ['Notary public commission', 'Licenses', 'An example commission. Choosing it does not mean the law requires it for this role.', ['Notary']],
  ['Certified Public Accountant (CPA) license', 'Licenses', 'An example license. Choosing it does not mean the law requires it for this role.', ['CPA license']],
  ['Insurance producer license', 'Licenses', 'An example license. Choosing it does not mean the law requires it for this role.'],
];

const LANGUAGES: Row[] = [
  ['English', 'Languages'], ['Spanish', 'Languages'], ['French', 'Languages'], ['German', 'Languages'], ['Mandarin Chinese', 'Languages', undefined, ['Mandarin']],
  ['Cantonese', 'Languages'], ['Vietnamese', 'Languages'], ['Tagalog', 'Languages', undefined, ['Filipino']], ['Arabic', 'Languages'], ['Korean', 'Languages'],
  ['Portuguese', 'Languages'], ['Russian', 'Languages'], ['Hindi', 'Languages'], ['American Sign Language', 'Languages', undefined, ['ASL']],
];

const PHYSICAL: Row[] = [
  ['Sitting for long periods', 'Physical'], ['Standing for long periods', 'Physical'], ['Walking or moving around', 'Physical'],
  ['Lifting up to 25 pounds', 'Physical'], ['Lifting up to 50 pounds', 'Physical'], ['Repetitive hand movements', 'Physical', undefined, ['Keyboard use']],
  ['Looking at a screen for long periods', 'Physical'], ['Driving', 'Physical'],
];

const CONDITIONS: Row[] = [
  ['Office environment', 'Working conditions'], ['Open-plan workspace', 'Working conditions'], ['Warehouse or plant environment', 'Working conditions'],
  ['Outdoor work', 'Working conditions'], ['Exposure to noise', 'Working conditions'], ['Irregular hours or on-call', 'Working conditions', undefined, ['On-call']],
  ['Remote or home office', 'Working conditions'], ['Customer-facing environment', 'Working conditions'],
];

const FLSA: Row[] = [
  ['Exempt (employer classification)', 'Employer classification', 'Your own classification. EPN does not decide whether a position is exempt.'],
  ['Non-exempt (employer classification)', 'Employer classification', 'Your own classification. EPN does not decide whether a position is non-exempt.'],
  ['Not yet determined (review with a payroll or legal advisor)', 'Employer classification', 'Use this until your payroll or legal advisor has confirmed the classification.'],
];

const BENEFITS: Row[] = [
  ['Eligible for the company’s standard benefits', 'Benefits'], ['Eligible after a waiting period', 'Benefits'], ['Eligible for part-time benefits', 'Benefits'],
  ['Not eligible for benefits', 'Benefits'], ['Follows the company benefits policy', 'Benefits'], ['Depends on hours worked', 'Benefits'],
];

const DEPARTMENTS: Row[] = [
  ['Executive', 'Departments'], ['Finance', 'Departments'], ['Accounting', 'Departments'], ['Payroll', 'Departments'], ['Human Resources', 'Departments', undefined, ['HR']],
  ['Operations', 'Departments'], ['Sales', 'Departments'], ['Marketing', 'Departments'], ['Customer Support', 'Departments', undefined, ['Customer Service']],
  ['Information Technology', 'Departments', undefined, ['IT']], ['Engineering', 'Departments'], ['Legal', 'Departments'], ['Administration', 'Departments'],
];

const INDICATORS: Row[] = [
  ['Payroll accuracy rate', 'Indicators'], ['On-time payroll submission', 'Indicators'], ['Customer satisfaction score', 'Indicators', undefined, ['CSAT']],
  ['Ticket resolution time', 'Indicators'], ['First-contact resolution rate', 'Indicators'], ['Budget variance', 'Indicators'],
  ['Audit findings closed on time', 'Indicators'], ['Training completion rate', 'Indicators'], ['Project delivery on schedule', 'Indicators'],
  ['Defects found after release', 'Indicators'], ['Revenue against target', 'Indicators'], ['Team turnover', 'Indicators'],
];

const WORK_ARRANGEMENT: Row[] = [
  ['On-site', 'Work arrangement'], ['Hybrid', 'Work arrangement'], ['Remote', 'Work arrangement'], ['Field-based', 'Work arrangement'], ['Flexible', 'Work arrangement'],
];

const TRAVEL: Row[] = [
  ['None', 'Travel'], ['Occasional, up to 10%', 'Travel'], ['Up to 25%', 'Travel'], ['Up to 50%', 'Travel'], ['More than 50%', 'Travel'],
];

export const MOCK_REFERENCE_OPTIONS: MockReferenceOptionRecord[] = [
  ...make('JobFamily', 'JF', FAMILIES, 'A group of related jobs.'),
  ...make('CareerLevel', 'CL', LEVELS, 'A career level.'),
  ...make('ManagementLevel', 'ML', MANAGEMENT, 'How much of other people’s work the role is accountable for.'),
  ...make('EducationLevel', 'ED', EDUCATION, 'A level of formal education.'),
  ...make('ExperienceRange', 'EX', EXPERIENCE, 'A range of years of relevant experience.'),
  ...make('WorkArrangement', 'WA', WORK_ARRANGEMENT, 'Where the work is usually done.'),
  ...make('TravelRequirement', 'TR', TRAVEL, 'How much of the working time is spent travelling.'),
  ...make('Skill', 'SK', SKILLS, 'A skill people use in the role.'),
  ...make('Certification', 'CE', CERTIFICATIONS, EXAMPLE_CREDENTIAL),
  ...make('License', 'LI', LICENSES, EXAMPLE_CREDENTIAL),
  ...make('Language', 'LA', LANGUAGES, 'A language the role uses.'),
  ...make('PhysicalRequirement', 'PR', PHYSICAL, 'A physical demand of the role.'),
  ...make('WorkingCondition', 'WC', CONDITIONS, 'A condition the work is done in.'),
  ...make('FlsaClassification', 'FL', FLSA, 'An employer classification.'),
  ...make('BenefitEligibility', 'BE', BENEFITS, 'How benefits apply to the role.'),
  ...make('RecommendedDepartment', 'RD', DEPARTMENTS, 'A common department name.'),
  ...make('PerformanceIndicator', 'PI', INDICATORS, 'A generic way to see how the role is doing.'),
];
